/**
Project: Voice Panel (c)
Title: Routing Policy List 
Description: Component to display policies and map related to regions and to change, enable and disable the policies.
Copyrights: This file is subject to the terms and conditions defined in file 'LICENSE.txt', which is part of this source code package.
*/
import React, { Component } from "react";
import { connect } from "react-redux";
import axios from "axios";
import { CloseIcon, Dialog, Flex } from "@fluentui/react-northstar";
import { Grid, Row, Col } from "react-flexbox-grid";
import PageUtil from "../../../../core/helpers/page-util";
import ResponseUtil from "../../../../core/helpers/response-util";
import IntlUtil from "../../../../core/helpers/intl-util";
import PageLoader from "../../common/helpers/page-loader";
import AlertSuccessMessage from "../../common/helpers/alert-success-message";
import AlertErrorMessage from "../../common/helpers/alert-error-message";
import PageExitGuard from "../../common/helpers/page-exit-guard";
import { AppConfigProps } from "../../../../core/settings/app-config";
import { manageError } from "../../../../core/actions/common-actions";
import {
  showPageWaitDialog,
  hidePageWaitDialog,
} from "../../common/actions/general-actions";
import {
  getRoutingPolicies,
  selectRoutingPolicy,
  getVoiceRoutes,
  enableRoutingPolicy,
  disableRoutingPolicy,
  disableVoiceRoutesUpdateButton,
  updateVoiceRoutes,
  resetRoutingData,
} from "../actions/routing-actions";
import { sortBy } from "lodash";
import RoutingPolicyList from "./routing-policy-list";
import VoiceRouteList from "./voice-route-list";
import VoiceRouteMap from "./voice-route-map";
import { VoiceAdminConstants } from "../../common/settings/voice-admin-constants";
import { VoiceAdminURLProps } from "../../common/settings/voice-admin-urls";
import { AppPageTitle } from "../../../../core/components/app-page-title";
import Setup from "../../voice-setup/components/setup";
import { setVoiceSetupProgressRegions } from "../../voice-setup/actions/voice-setup-actions";
import PageOverlay from "../../common/helpers/page-overlay";

class RoutingPage extends Component {
  _isMounted = false;
  _axiosSource = axios.CancelToken.source();
  _cancelToken = { cancelToken: this._axiosSource.token };
  _intl_ns_common = "common";
  _intl_ns_vpa_routing = "vpa_routing";

  constructor(props) {
    super(props);
    this.state = {
      isPageDataFetched: false,
      errorMessage: null,
      successMessage: null,
      showAlert: false,
      errorDetails: null,
      isError: false,
      isRoutingEnableDialogOpen: false,
      enablePolicyObj: null,
      setUpInProgress: false,
    };
  }

  setStateAsync = (state) => {
    if (this._isMounted) {
      return new Promise((resolve) => {
        this.setState(state, resolve);
      });
    }
  };

  async componentDidMount() {
    this._isMounted = true;
    PageUtil.scrollToTop();
    /*
    const script = document.createElement("script");
    script.async = true;
    script.src = "https://hosting.simplemaps.com/users/HAX0tjcJ7z2H/mapdata.js";
    document.head.appendChild(script);
    */
    await this.loadPageData();
  }

  componentWillUnmount() {
    this.props.resetRoutingData();
    this.props.hidePageWaitDialog();
    this._isMounted = false;
    this._axiosSource.cancel(
      IntlUtil.getText(
        this._intl_ns_common,
        "notification.warning.requestCancelled"
      )
    );
  }

  loadPageData = async () => {
    await this.setStateAsync({ isPageDataFetched: false });
    this.props.hidePageWaitDialog();
    await this.clearAlert();
    await this.getPolicies();
    this.props.disableVoiceRoutesUpdateButton();
    await this.setStateAsync({ isPageDataFetched: true });
  };

  getPolicies = async () => {
    try {
      await this.props.getRoutingPolicies(this._cancelToken);
      if (this.props.routingPolicies && this.props.routingPolicies.length > 0) {
        const selectedPolicy = this.props.routingPolicies[0];
        await this.getRoutes(selectedPolicy);
      }
    } catch (err) {
      this.props.hidePageWaitDialog();
      const errorStatus = ResponseUtil.getStatus(err);
      if (
        errorStatus &&
        errorStatus === AppConfigProps.httpStatusCode.badRequest
      ) {
        const primeError = ResponseUtil.getPrimeError(err);
        if (
          primeError &&
          primeError.code ===
            VoiceAdminConstants.SERVER_ERROR_CODE.VOICE_SETUP_NOTCOMPLETED
        ) {
          this.props.disableVoiceRoutesUpdateButton();
          this.props.history.push(VoiceAdminURLProps.setup);
          return;
        }
      } else {
        await this.setState({ isError: true });
        await manageError(err, this.props.history);
      }
    }
  };

  getRoutes = async (selectedPolicy) => {
    try {
      if (selectedPolicy) {
        const selectedPolicyIdentity = selectedPolicy.policyId;
        await this.props.getVoiceRoutes(
          selectedPolicyIdentity,
          this._cancelToken
        );
        await this.props.selectRoutingPolicy(selectedPolicy);
      }
    } catch (err) {
      this.props.hidePageWaitDialog();
      const errorStatus = ResponseUtil.getStatus(err);
      if (
        errorStatus &&
        errorStatus === AppConfigProps.httpStatusCode.badRequest
      ) {
        const primeError = ResponseUtil.getPrimeError(err);
        if (
          primeError &&
          primeError.code ===
            VoiceAdminConstants.SERVER_ERROR_CODE.VOICE_SETUP_NOTCOMPLETED
        ) {
          this.props.disableVoiceRoutesUpdateButton();
          this.props.history.push(VoiceAdminURLProps.setup);
          return;
        }
      } else {
        await this.setState({ isError: true });
        await manageError(err, this.props.history);
      }
    }
  };

  changePolicy = async (policyIdentity) => {
    await this.clearAlert();
    if (policyIdentity && this.props.routingPolicies) {
      const selectedPolicy = this.props.routingPolicies.find((policy) => {
        return policy.policyId === policyIdentity;
      });
      if (selectedPolicy) {
        await this.props.selectRoutingPolicy(selectedPolicy);
      }
    }
    this.props.showPageWaitDialog(
      IntlUtil.getText(
        this._intl_ns_vpa_routing,
        "notification.info.pleaseWaitRetrievingData"
      )
    );
    if (policyIdentity && this.props.routingPolicies) {
      const selectedPolicy = this.props.routingPolicies.find((policy) => {
        return policy.policyId === policyIdentity;
      });
      if (selectedPolicy && selectedPolicy?.policyStatus === 1) {
        await this.getRoutes(selectedPolicy);
      } else if (selectedPolicy && selectedPolicy?.policyStatus === 0) {
        await this.props.selectRoutingPolicy(selectedPolicy);
      }
    }
    this.props.disableVoiceRoutesUpdateButton();
    this.props.hidePageWaitDialog();
  };

  disablePolicy = async (policy) => {
    await this.clearAlert();
    if (policy && policy.policyStatus === 1) {
      this.props.disableVoiceRoutesUpdateButton();
      this.props.showPageWaitDialog(
        IntlUtil.getText(
          this._intl_ns_vpa_routing,
          "notification.info.pleaseWaitUpdatingData"
        )
      );
      const data = {
        policyId: policy.policyId,
        policyStatus: policy.policyStatus,
        updateStatus: 0,
      };
      await disableRoutingPolicy(policy.policyId, data, this._cancelToken)
        .then(async (res) => {
          if (
            res &&
            res.status === AppConfigProps.httpStatusCode.ok &&
            res.data &&
            res.data.result
          ) {
            await this.props.getRoutingPolicies(this._cancelToken);
            await this.props.getVoiceRoutes(
              res.data.result.policyId,
              this._cancelToken
            );
            await this.props.selectRoutingPolicy(res.data.result);
            await this.setStateAsync({
              successMessage: this.getPolicyNotificationText(
                "notification.success.policyDisable",
                this.props.selectedRoutingPolicy.policyId
              ),
              showAlert: true,
            });
            this.props.hidePageWaitDialog();
          } else {
            await this.setStateAsync({
              errorMessage: this.getPolicyNotificationText(
                "notification.error.policyDisableFailed",
                this.props.selectedRoutingPolicy.policyId
              ),
              showAlert: true,
            });
            this.props.hidePageWaitDialog();
          }
        })
        .catch(async (err) => {
          this.props.hidePageWaitDialog();
          const errorStatus = ResponseUtil.getStatus(err);
          if (
            errorStatus &&
            errorStatus === AppConfigProps.httpStatusCode.badRequest
          ) {
            const primeError = ResponseUtil.getPrimeError(err);
            if (primeError) {
              if (
                primeError.code ===
                VoiceAdminConstants.SERVER_ERROR_CODE.VOICE_SETUP_NOTCOMPLETED
              ) {
                this.props.disableVoiceRoutesUpdateButton();
                this.props.history.push(VoiceAdminURLProps.setup);
                return;
              } else if (
                primeError.code ===
                VoiceAdminConstants.SERVER_ERROR_CODE
                  .ROUTING_POLICY_DISABLE_FAILED
              ) {
                await this.setStateAsync({
                  errorDetails: primeError.message,
                });
              }
            }
            await this.setStateAsync({
              errorMessage: this.getPolicyNotificationText(
                "notification.error.policyDisableFailed",
                this.props.selectedRoutingPolicy.policyId
              ),
              showAlert: true,
            });
          } else {
            await this.setState({ isError: true });
            await manageError(err, this.props.history);
          }
        });
    }
  };

  enablePolicy = async (policy) => {
    await this.clearAlert();
    await this.props.setVoiceSetupProgressRegions([policy?.regionName]);
    await this.setStateAsync({
      isRoutingEnableDialogOpen: true,
      enablePolicyObj: policy,
    });
  };

  renderEnableRoutingDialog = () => {
    if (this.state.isRoutingEnableDialogOpen === true) {
      return (
        <Dialog
          backdrop={true}
          header={this.state.enablePolicyObj?.policyId}
          open={this.state.isRoutingEnableDialogOpen}
          content={
            <div className="m-0">
              <Setup
                {...this.props}
                isRoutingEnablePage={true}
                setUpInProgress={async () => {
                  await this.setStateAsync({ setUpInProgress: true });
                }}
                setUpFailure={async () => {
                  await this.setStateAsync({ setUpInProgress: false });
                }}
                setUpCompleted={this.setUpCompleted}
                closeButtonDisabled={this.state.setUpInProgress}
                closeButtonOnClick={async () =>
                  await this.setStateAsync({ isRoutingEnableDialogOpen: false })
                }
              />
            </div>
          }
          className="vpa-routing-enable-dialog"
        />
      );
    }
  };

  setUpCompleted = async () => {
    await this.props.getRoutingPolicies(this._cancelToken);
    await this.props.getVoiceRoutes(
      this.state.enablePolicyObj?.policyId,
      this._cancelToken
    );

    if (this.props.routingPolicies?.length > 0) {
      let policyObj = this.props.routingPolicies.find(
        (pol) => pol?.policyId === this.state.enablePolicyObj?.policyId
      );
      if (policyObj) await this.props.selectRoutingPolicy(policyObj);
    }
    await this.setStateAsync({
      successMessage: this.getPolicyNotificationText(
        "notification.success.policyEnable",
        this.props.selectedRoutingPolicy.policyId
      ),
      showAlert: true,
    });
    await this.setStateAsync({
      setUpInProgress: false,
      isRoutingEnableDialogOpen: false,
    });
    this.props.hidePageWaitDialog();
  };

  updateVoiceRoutes = async () => {
    await this.clearAlert();
    if (
      this.props.selectedRoutingPolicy &&
      this.props.selectedRoutingPolicy.policyId &&
      this.props.voiceRoutes &&
      this.props.voiceRoutes.length > 0
    ) {
      const routesData = await this.props.voiceRoutes.map((route) => {
        return {
          routeName: route.routeName,
          routePriority: route.previousPriority,
          routeStatus: route.previousStatus,
          updatePriority: route.routePriority,
          updateStatus: route.routeStatus,
        };
      });
      if (routesData && routesData.length > 0) {
        const unassignedRoutes = await routesData.filter((route) => {
          return route.updateStatus === 0;
        });
        if (routesData.length - unassignedRoutes.length < 2) {
          await this.setStateAsync({
            errorMessage: IntlUtil.getText(
              this._intl_ns_vpa_routing,
              "notification.error.routesRequired"
            ),
            showAlert: true,
          });
          PageUtil.scrollToTop();
        } else {
          this.props.disableVoiceRoutesUpdateButton();
          this.props.showPageWaitDialog(
            IntlUtil.getText(
              this._intl_ns_vpa_routing,
              "notification.info.pleaseWaitUpdatingData"
            )
          );
          try {
            await this.props.updateVoiceRoutes(
              this.props.selectedRoutingPolicy.policyId,
              routesData,
              this._cancelToken
            );
            await this.setStateAsync({
              successMessage: this.getPolicyNotificationText(
                "notification.success.routesUpdate",
                this.props.selectedRoutingPolicy.policyId
              ),
              showAlert: true,
            });
            this.props.hidePageWaitDialog();
          } catch (err) {
            this.props.hidePageWaitDialog();
            const errorStatus = ResponseUtil.getStatus(err);
            if (
              errorStatus &&
              errorStatus === AppConfigProps.httpStatusCode.badRequest
            ) {
              const primeError = ResponseUtil.getPrimeError(err);
              if (primeError) {
                if (
                  primeError.code ===
                  VoiceAdminConstants.SERVER_ERROR_CODE.VOICE_SETUP_NOTCOMPLETED
                ) {
                  this.props.disableVoiceRoutesUpdateButton();
                  this.props.history.push(VoiceAdminURLProps.setup);
                  return;
                } else if (
                  primeError.code ===
                  VoiceAdminConstants.SERVER_ERROR_CODE
                    .VOICE_ROUTES_UPDATE_FAILED
                ) {
                  await this.setStateAsync({
                    errorDetails: primeError.message,
                  });
                }
              }
              await this.setStateAsync({
                errorMessage: this.getPolicyNotificationText(
                  "notification.error.routesUpdateFailed",
                  this.props.selectedRoutingPolicy.policyId
                ),
                showAlert: true,
              });
            } else {
              await this.setState({ isError: true });
              await manageError(err, this.props.history);
            }
          }
        }
      }
    }
  };

  clearAlert = async () => {
    await this.setStateAsync({
      errorMessage: null,
      errorDetails: null,
      successMessage: null,
      showAlert: false,
    });
  };

  getPolicyNotificationText(keyId, policy) {
    if (keyId && policy) {
      return IntlUtil.getSubstituteText(this._intl_ns_vpa_routing, keyId, [
        {
          key: "<POLICY>",
          value: policy,
        },
      ]);
    } else return null;
  }

  render() {
    return (
      <>
        <AppPageTitle
          pageTitle={IntlUtil.getText(
            this._intl_ns_vpa_routing,
            "title.routing"
          )}
        />
        <PageLoader
          status={this.state.isPageDataFetched}
          size="medium"
          labelPosition="below"
          label={IntlUtil.getText(
            this._intl_ns_common,
            "content.loadingInprogress"
          )}
          type="full-center"
        >
          <Flex column className="p-t-5">
            {this.props.routingPolicies &&
            this.props.routingPolicies.length > 0 &&
            this.props.selectedRoutingPolicy &&
            this.props.voiceRoutes &&
            this.props.voiceRoutes.length > 0 ? (
              <>
                <Grid fluid className="page-grid">
                  {this.state.showAlert === true ? (
                    <Row>
                      <Col xs={12} className="m-b-5 m-t-10">
                        {this.state.errorMessage ? (
                          <AlertErrorMessage
                            content={this.state.errorMessage}
                            moreDetails={{
                              label: IntlUtil.getText(
                                this._intl_ns_vpa_routing,
                                "content.moreDetails"
                              ),
                              content: this.state.errorDetails,
                            }}
                          />
                        ) : null}
                        {this.state.successMessage ? (
                          <AlertSuccessMessage
                            content={this.state.successMessage}
                          />
                        ) : null}
                      </Col>
                    </Row>
                  ) : null}
                  <Row>
                    <Col xs={12} sm={12} md={12} lg={5} className="m-0">
                      <Grid fluid className="p-0">
                        <Row>
                          <Col xs={12}>
                            <RoutingPolicyList
                              changePolicy={this.changePolicy}
                              enablePolicy={this.enablePolicy}
                              disablePolicy={this.disablePolicy}
                              history={this.props.history}
                            />
                          </Col>
                        </Row>
                        <Row>
                          <Col xs={12}>
                            <VoiceRouteList
                              selectedRoutingPolicy={
                                this.props.selectedRoutingPolicy
                              }
                              voiceRoutes={this.props.voiceRoutes}
                              updateVoiceRoutes={this.updateVoiceRoutes}
                              history={this.props.history}
                            />
                          </Col>
                        </Row>
                      </Grid>
                    </Col>
                    <Col xs={12} sm={12} md={12} lg={7} className="m-b-15">
                      <VoiceRouteMap
                        voiceRoutes={
                          this.props.selectedRoutingPolicy?.policyStatus === 1
                            ? this.props.voiceRoutes
                            : []
                        }
                        history={this.props.history}
                      />
                    </Col>
                  </Row>
                </Grid>
              </>
            ) : (
              <Grid fluid className="page-grid">
                <Row>
                  <Col xs={12} className="m-t-10 m-b-300">
                    <AlertErrorMessage
                      content={
                        <>
                          {IntlUtil.getText(
                            this._intl_ns_vpa_routing,
                            "notification.error.routingRetrieveFailed"
                          )}
                        </>
                      }
                    />
                  </Col>
                </Row>
              </Grid>
            )}
          </Flex>
        </PageLoader>
        {this.props.history.location.pathname === VoiceAdminURLProps.routing ? (
          <PageExitGuard
            when={this.props.voiceRoutesUpdateButtonDisableStatus === false}
            navigate={(path) => this.props.history.push(path)}
            shouldBlockNavigation={(location) => {
              if (this.props.voiceRoutesUpdateButtonDisableStatus === false) {
                return true;
              }
              return false;
            }}
          />
        ) : (
          <PageExitGuard
            when={this.state.isError === false}
            navigate={(path) => this.props.history.push(path)}
            shouldBlockNavigation={(location) => {
              if (location.pathname) {
                return true;
              }

              return false;
            }}
          />
        )}
        {this.renderEnableRoutingDialog()}
      </>
    );
  }
}

const mapStateToProps = (state) => ({
  subscriberAccount: state.subscriberStore.subscriberAccount,
  routingPolicies: state.routingStore.routingPolicies,
  selectedRoutingPolicy: state.routingStore.selectedRoutingPolicy,
  voiceRoutes: sortBy(state.routingStore.voiceRoutes, ["routePriority"]),
  voiceRoutesUpdateButtonDisableStatus:
    state.routingStore.voiceRoutesUpdateButtonDisableStatus,
});

const mapActionToProps = {
  showPageWaitDialog,
  hidePageWaitDialog,
  getRoutingPolicies,
  selectRoutingPolicy,
  getVoiceRoutes,
  disableVoiceRoutesUpdateButton,
  updateVoiceRoutes,
  resetRoutingData,
  setVoiceSetupProgressRegions,
};

export default connect(mapStateToProps, mapActionToProps)(RoutingPage);
